import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaHome, FaSearch, FaFilePdf, FaDownload } from 'react-icons/fa';

const DocumentsAndDarfsScreen = () => {
    // Filtros
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedEmitenteId, setSelectedEmitenteId] = useState('');

    // Dados
    const [allDocuments, setAllDocuments] = useState([]);
    const [currentDocuments, setCurrentDocuments] = useState([]);
    const [emitentes, setEmitentes] = useState([]);

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [documentsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const [loading, setLoading] = useState(false);
    const [loadingEmitentes, setLoadingEmitentes] = useState(false);

    // URLs
    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const URL_PUBLICADOR = process.env.REACT_APP_URL_PUBLICADOR;
    const URL_DOCUMENTOS = process.env.REACT_APP_URL_DOCUMENTOS;

    const navigate = useNavigate();

    // Datas padrão + emitente
    useEffect(() => {
        const storedEmitenteId = localStorage.getItem('userIdEmitente');
        if (storedEmitenteId) {
            setSelectedEmitenteId(storedEmitenteId);
            const today = new Date();
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(today.getDate() - 30);
            const toISO = (d) => d.toISOString().slice(0, 10);
            setEndDate(toISO(today));
            setStartDate(toISO(thirtyDaysAgo));
        } else {
            toast.error('ID do emitente não encontrado. Faça login novamente.');
        }
    }, []);

    // Emitentes
    useEffect(() => {
        const fetchEmitentes = async () => {
            setLoadingEmitentes(true);
            try {
                const { data } = await axios.get(`${URL_BACKEND}/emitentesAll/999/999`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
                });
                setEmitentes(data);
                if (data.length > 0 && selectedEmitenteId === '') {
                    setSelectedEmitenteId(data[0].id);
                }
            } catch (error) {
                console.error('Erro ao buscar emitentes:', error);
                toast.error('Erro ao carregar a lista de emitentes.');
            } finally {
                setLoadingEmitentes(false);
            }
        };
        fetchEmitentes();
    }, [URL_BACKEND, selectedEmitenteId]);

    // Buscar Documentos e DARFs
    const fetchDocumentsAndDarfs = async () => {
        if (!selectedEmitenteId || !startDate || !endDate) {
            toast.error('Selecione um emitente e preencha as datas.');
            return;
        }

        toast.success('Rotina em construção.');
        return;

        setLoading(true);
        try {
            // ajuste aqui para os seus filtros reais quando disponíveis
            const { data } = await axios.get(`${URL_PUBLICADOR}/documentos/app`, {
                params: { cnpj: '17.318.340/0001-68' },
                headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
            });
            setAllDocuments(data);
            setTotalPages(Math.ceil(data.length / documentsPerPage));
            setCurrentPage(1);
            toast.success(`Documentos e DARFs carregados com sucesso! (${data.length} registros)`);
        } catch (error) {
            console.error('Erro ao buscar documentos e DARFs:', error);
            toast.error(error.response?.data?.message || 'Erro ao carregar documentos e DARFs.');
            setAllDocuments([]);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    // Atualiza página atual
    useEffect(() => {
        const end = currentPage * documentsPerPage;
        const start = end - documentsPerPage;
        setCurrentDocuments(allDocuments.slice(start, end));
    }, [allDocuments, currentPage, documentsPerPage]);

    // Busca quando filtros mudam
    useEffect(() => {
        if (selectedEmitenteId && startDate && endDate) {
            fetchDocumentsAndDarfs();
        }
    }, [selectedEmitenteId, startDate, endDate, URL_BACKEND]);

    // Paginação
    const goToNextPage = () => currentPage < totalPages && setCurrentPage(p => p + 1);
    const goToPreviousPage = () => currentPage > 1 && setCurrentPage(p => p - 1);
    const goToPage = (n) => setCurrentPage(n);

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const max = 5;
        let start = Math.max(1, currentPage - Math.floor(max / 2));
        let end = Math.min(totalPages, start + max - 1);
        if (end - start + 1 < max) start = Math.max(1, end - max + 1);
        for (let i = start; i <= end; i++) pageNumbers.push(i);

        return (
            <ul style={ui.paginationList}>
                {start > 1 && (
                    <>
                        <li style={ui.paginationItem} onClick={() => goToPage(1)}>
                            <button style={ui.paginationButton}>1</button>
                        </li>
                        {start > 2 && <li style={ui.paginationEllipsis}>…</li>}
                    </>
                )}
                {pageNumbers.map(n => (
                    <li key={n} style={ui.paginationItem} onClick={() => goToPage(n)}>
                        <button style={{ ...ui.paginationButton, ...(currentPage === n ? ui.paginationButtonActive : {}) }}>
                            {n}
                        </button>
                    </li>
                ))}
                {end < totalPages && (
                    <>
                        {end < totalPages - 1 && <li style={ui.paginationEllipsis}>…</li>}
                        <li style={ui.paginationItem} onClick={() => goToPage(totalPages)}>
                            <button style={ui.paginationButton}>{totalPages}</button>
                        </li>
                    </>
                )}
            </ul>
        );
    };

    // Ações
    const handleOpenPdf = (caminhoPdf) => {
        if (caminhoPdf) {
            window.open(`${URL_DOCUMENTOS}/${caminhoPdf}`, '_blank');
        } else {
            toast.error('Caminho do PDF não disponível para este documento.');
        }
    };

    const handleDownloadPdf = async (caminhoPdf, nomeArquivo) => {
        if (!caminhoPdf) return toast.error('Caminho do PDF não disponível para este documento.');
        try {
            toast.loading('Preparando download...', { id: 'downloadPdf' });
            const resp = await axios.get(`${URL_PUBLICADOR}/${caminhoPdf}`, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
            });
            const url = window.URL.createObjectURL(new Blob([resp.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nomeArquivo || 'documento.pdf');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Download iniciado!', { id: 'downloadPdf' });
        } catch (error) {
            console.error('Erro ao baixar PDF:', error);
            toast.error('Erro ao baixar o documento PDF.', { id: 'downloadPdf' });
        }
    };

    return (
        <div style={ui.page}>
            {/* Header com gradiente (mesmo padrão) */}
            <div style={ui.headerBar}>
                <h1 style={ui.headerTitle}>Documentos e DARFs</h1>
                <button onClick={() => navigate('/dashboard')} style={ui.headerButton}>
                    <FaHome style={{ marginRight: 8 }} /> Voltar
                </button>
            </div>

            {/* Filtros */}
            <div style={ui.card}>
                <h3 style={ui.sectionTitle}>Filtros</h3>
                <div style={ui.filtersRow}>
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Data Inicial</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={ui.input} />
                    </div>
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Data Final</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={ui.input} />
                    </div>
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Emitente</label>
                        <select
                            value={selectedEmitenteId}
                            onChange={e => setSelectedEmitenteId(e.target.value)}
                            style={ui.select}
                            disabled={loadingEmitentes}
                        >
                            {loadingEmitentes ? (
                                <option value="">Carregando...</option>
                            ) : (
                                <>
                                    <option value="">Selecione...</option>
                                    {emitentes.map(em => (
                                        <option key={em.id} value={em.id}>{em.nome}</option>
                                    ))}
                                </>
                            )}
                        </select>
                        {loadingEmitentes && (
                            <div style={ui.helpText}>
                                <FaSpinner className="fa-spin" style={{ marginRight: 4 }} />
                                Carregando emitentes...
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button onClick={fetchDocumentsAndDarfs} disabled={loading || loadingEmitentes} style={ui.buttonPrimary}>
                            {loading ? <><FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Buscando…</> : <><FaSearch style={{ marginRight: 8 }} /> Buscar Documentos</>}
                        </button>
                    </div>
                </div>
            </div>

            {/* Resultados */}
            <div style={ui.card}>
                <h3 style={ui.sectionTitle}>Resultados</h3>

                {loading && <p style={ui.muted}>Carregando documentos…</p>}
                {!loading && allDocuments.length === 0 && <p style={ui.errorText}>Nenhum documento ou DARF encontrado com os filtros selecionados.</p>}

                {!loading && allDocuments.length > 0 && (
                    <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={ui.table}>
                                <thead>
                                    <tr>
                                        <th style={ui.th}>ID Doc</th>
                                        <th style={ui.th}>CNPJ Cliente</th>
                                        <th style={ui.th}>Cliente</th>
                                        <th style={ui.th}>Documento</th>
                                        <th style={ui.th}>Status</th>
                                        <th style={ui.th}>App</th>
                                        <th style={ui.th}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentDocuments.map(doc => (
                                        <tr key={doc.id} style={ui.tr}>
                                            <td style={ui.td}>{doc.id}</td>
                                            <td style={ui.td}>{doc.cnpj_cliente}</td>
                                            <td style={ui.td}>{doc.nome_cliente}</td>
                                            <td style={ui.td}>{doc.tipo_detectado}</td>
                                            <td style={ui.td}>{doc.status}</td>
                                            <td style={ui.td}>{doc.disponibiliza_app}</td>
                                            <td style={ui.tdActions}>
                                                {doc.caminho_arquivo && (
                                                    <>
                                                        <button
                                                            title="Visualizar PDF"
                                                            onClick={() => handleOpenPdf(doc.caminho_arquivo)}
                                                            style={ui.actionPrimary}
                                                        >
                                                            <FaFilePdf />
                                                        </button>
                                                        <button
                                                            title="Download PDF"
                                                            onClick={() => handleDownloadPdf(doc.caminho_arquivo, `documento_${doc.id}.pdf`)}
                                                            style={ui.actionSuccess}
                                                        >
                                                            <FaDownload />
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div style={ui.paginationContainer}>
                                <button onClick={goToPreviousPage} disabled={currentPage === 1} style={ui.paginationNav}>
                                    Anterior
                                </button>
                                {renderPageNumbers()}
                                <button onClick={goToNextPage} disabled={currentPage === totalPages} style={ui.paginationNav}>
                                    Próxima
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

/** 🎨 UI – mesmo padrão do ClienteModal */
const ui = {
    page: {
        padding: 24,
        backgroundColor: '#f8fafc',
        minHeight: '95vh',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        boxSizing: 'border-box',
        marginBottom: 40
    },
    headerBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 24,
        marginBottom: 24,
        border: '1px solid #e5e7eb',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        color: '#fff',
    },
    headerTitle: {
        margin: 0,
        fontSize: '1.5rem',
        fontWeight: 600,
    },
    headerButton: {
        backgroundColor: 'rgba(255,255,255,0.15)',
        color: '#fff',
        padding: '10px 16px',
        borderRadius: 10,
        border: '2px solid rgba(255,255,255,0.25)',
        cursor: 'pointer',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
    },

    card: {
        backgroundColor: '#ffffff',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        padding: 20,
        marginBottom: 24,
    },
    sectionTitle: {
        margin: '0 0 16px 0',
        fontSize: '1.125rem',
        fontWeight: 600,
        color: '#1f2937',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: 8,
    },

    filtersRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 200,
        flex: 1,
    },
    label: {
        marginBottom: 6,
        fontWeight: 500,
        color: '#374151',
        fontSize: '0.875rem',
    },
    helpText: {
        fontSize: '0.75rem',
        color: '#6b7280',
        marginTop: 4,
        display: 'flex',
        alignItems: 'center',
    },
    input: {
        padding: '12px 16px',
        borderRadius: 8,
        border: '2px solid #e5e7eb',
        fontSize: '0.875rem',
        backgroundColor: '#ffffff',
        outline: 'none',
        transition: 'all 0.2s ease',
    },
    select: {
        padding: '12px 16px',
        borderRadius: 8,
        border: '2px solid #e5e7eb',
        fontSize: '0.875rem',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        outline: 'none',
        transition: 'all 0.2s ease',
    },
    buttonPrimary: {
        backgroundColor: '#667eea',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: 8,
        border: '2px solid #667eea',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.875rem',
        display: 'flex',
        alignItems: 'center',
    },

    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: '0 8px',
    },
    th: {
        backgroundColor: '#e9ecef',
        padding: 8,
        textAlign: 'left',
        color: '#555',
        fontWeight: 'bold',
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: '2px solid #dee2e6',
    },
    tr: {
        backgroundColor: '#fdfdfe',
        transition: 'background-color 0.2s ease',
        minHeight: 45,
    },
    td: {
        padding: 8,
        borderBottom: '1px solid #e9ecef',
        color: '#495057',
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        lineHeight: 1.3,
    },
    tdActions: {
        padding: '6px 8px',
        borderBottom: '1px solid #e9ecef',
        display: 'flex',
        gap: 6,
        alignItems: 'center',
    },

    // Ações
    actionPrimary: {
        backgroundColor: '#3b82f6',
        color: '#fff',
        border: 'none',
        padding: 8,
        borderRadius: 8,
        cursor: 'pointer',
    },
    actionSuccess: {
        backgroundColor: '#10b981',
        color: '#fff',
        border: 'none',
        padding: 8,
        borderRadius: 8,
        cursor: 'pointer',
    },

    // Paginação
    paginationContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 20,
        gap: 10,
    },
    paginationNav: {
        backgroundColor: '#667eea',
        color: '#fff',
        padding: '10px 18px',
        borderRadius: 8,
        border: 'none',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    paginationList: {
        display: 'flex',
        listStyle: 'none',
        margin: 0,
        padding: 0,
        gap: 6,
    },
    paginationItem: { cursor: 'pointer' },
    paginationButton: {
        backgroundColor: '#f0f2f5',
        color: '#333',
        padding: '10px 15px',
        borderRadius: 8,
        border: '1px solid #ddd',
        fontSize: '0.95rem',
        minWidth: 40,
        cursor: 'pointer',
    },
    paginationButtonActive: {
        backgroundColor: '#667eea',
        color: '#fff',
        borderColor: '#667eea',
    },

    muted: { color: '#6b7280' },
    errorText: { color: '#ef4444', fontWeight: 600 },
};

export default DocumentsAndDarfsScreen;
